import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Upload, AccountCircle, Explore as ExploreIcon } from '@mui/icons-material';
import { Sun, Moon, Menu, X, Search as LucideSearch, Users } from 'lucide-react';
import Logo from '../../assets/Images/Logo.png';
import api from '../../api/axiosConfig';

export default function Sidebar({ isSidebarOpen, setIsSidebarOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('Sidebardash');

  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const searchTimerRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    if (isSidebarOpen && window.innerWidth < 768) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
  }, [isSidebarOpen]);

  //Debounced Search
  const debouncedSearch = useCallback((query) => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const response = await api.get(`/properties/?search=${encodeURIComponent(query)}`);
        const data = response.data.results || response.data;
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Search failed:', err);
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
  }, []);

  // Fetch Suggestions
  const fetchSuggestions = useCallback(async (query) => {
    if (!query.trim() || query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await api.get(`/search-suggestions/?q=${encodeURIComponent(query)}`);
      setSuggestions(res.data);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery);
      fetchSuggestions(searchQuery);
    } else {
      setSearchResults([]);
      setSuggestions([]);
    }
  }, [searchQuery, debouncedSearch, fetchSuggestions]);

  const handleSearchResultClick = useCallback((propertyId) => {
      if (!propertyId) return;
      setIsSearchPanelOpen(false);
      
      const currentPath = location.pathname;
      const targetPath = currentPath.includes('exploredash') ? '/exploredash' : '/homedash';
      
      navigate(`${targetPath}?property_id=${encodeURIComponent(propertyId)}`, {
        state: { scrollToProperty: propertyId }
      });
    }, [location.pathname, navigate]);

  const handleSeeAllResults = () => {
    setIsSearchPanelOpen(false);
    navigate(`/explore?search=${encodeURIComponent(searchQuery)}`);
  };

  const navItems = [
    { name: 'Home', path: '/', icon: <Home /> },
    { name: 'Explore', path: '/explore', icon: <ExploreIcon /> },
    { name: 'About', path: '/about', icon: <Users/> },
    { name: 'Upload', path: '/login', icon: <Upload /> },
    { name: 'Profile', path: '/login', icon: <AccountCircle /> }
  ];

  const isActive = (path) => location.pathname === path;

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  }, [location, setIsSidebarOpen]);

  return (
    <>
      {/* Mobile Menu Button */}
      {window.innerWidth < 768 && (
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="md:hidden fixed top-4 left-4 z-30 p-2 rounded-lg bg-transparent text-[var(--text-black)] shadow-lg border-none"
          aria-label={t('sidebar.navigation.Menu') || 'Toggle menu'}
        >
          <Menu size={24} />
        </button>
      )}

      {isSidebarOpen && window.innerWidth < 768 && (
        <div
          className="fixed inset-0 bg-black/40 z-20 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {isSearchPanelOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50"
          onClick={() => setIsSearchPanelOpen(false)}
        />
      )}

      {isSearchPanelOpen && (
        <div
          className="fixed top-0 left-0 z-50 w-full max-w-xs sm:w-80 h-screen bg-[var(--bg-black)] border-r border-[var(--border)] overflow-y-auto shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-4 border-b border-[var(--border)]">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-[var(--text-black)]">{t('sidebar.search.resultsTitle')}</h3>
              <button
                onClick={() => setIsSearchPanelOpen(false)}
                className="text-[var(--text-black)] hover:text-pink-500 bg-transparent border-none"
              >
                <X size={20} />
              </button>
            </div>
            <div className="mt-4 relative">
              <div className="relative">
                <LucideSearch className="absolute left-3 top-1.5 -translate-y-1/2 text-[var(--text-black)]" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('sidebar.search.placeholder')}
                  className="w-full pl-10 pr-4 py-2 bg-[var(--bg-black)] border border-[var(--border)] rounded-lg text-[var(--text-black)] focus:outline-none focus:ring-1 focus:ring-pink-500"
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1.5 -translate-y-1/2 text-pink-500 bg-transparent border-none"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {searchLoading && !searchQuery ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-pink-500"></div>
            </div>
          ) : suggestions.length > 0 && searchQuery ? (
            <div className="p-4">
              <h4 className="text-xs font-semibold text-gray-400 mb-2 px-2">
                {t('sidebar.search.suggestionsTitle')}
              </h4>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={async () => {            
                      setSearchQuery(suggestion);
                      setSuggestions([]);

                      setSearchLoading(true);
                      try {
                        const res = await api.get(`/properties/?search=${encodeURIComponent(suggestion)}`);
                        const data = res.data.results || res.data;
                        const results = Array.isArray(data) ? data : [];
                        setSearchResults(results);

                        if (results.length === 1) {
                          setIsSearchPanelOpen(false);
                          navigate(`/property/${results[0].id}`);
                        }
                      } catch (err) {
                        console.error('Search from suggestion failed:', err);
                        setSearchResults([]);
                      } finally {
                        setSearchLoading(false);
                      }
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-[var(--text-black)] hover:bg-white/10 text-sm"
                  >
                    {suggestion}
                  </button>
                ))}

              </div>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="p-4 space-y-4">
              {searchResults.slice(0, 5).map((result) => (
                <div
                  key={result.id}
                  onClick={() => handleSearchResultClick(result.id)}
                  className="p-3 bg-[var(--bg-black)] border border-[var(--border)] rounded-lg cursor-pointer hover:bg-[var(--button-hover)] transition"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={result.images?.[0]?.image || '/fallback.jpg'}
                      alt={result.title}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-[var(--text-black)] truncate">{result.title}</h4>
                      <p className="text-xs text-gray-500 truncate">{result.location}</p>
                    </div>
                  </div>
                </div>
              ))}
              {searchResults.length > 5 && (
                <button
                  onClick={handleSeeAllResults}
                  className="w-full mt-4 py-2.5 bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 transition"
                >
                  {t('sidebar.search.seeAll')}
                </button>
              )}
            </div>
          ) : searchQuery ? (
            <div className="text-center py-10 text-[var(--text-black)]">
              {t('sidebar.search.noResults')}
            </div>
          ) : (
            <div className="text-center py-10 text-[var(--text-black)] opacity-60">
              {t('sidebar.search.placeholder')}
            </div>
          )}
        </div>
      )}

      <aside
        className={`fixed md:sticky top-0 left-0 z-20 h-screen bg-sidebar transition-all duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:w-16 lg:w-20 xl:w-24`}
      >
        <div className="flex flex-col items-start py-6 px-4 h-full">
          <div className="flex items-center mb-6 whitespace-nowrap">
            <img src={Logo} alt="Pongo Logo" className="h-8 w-auto mr-0" />
            <span className="text-pink-500 font-bold text-xl">Pongo</span>
          </div>

          <button
            onClick={() => setIsSearchPanelOpen(true)}
            className="flex items-center w-36 mb-6 px-3 py-1.5 rounded-full bg-[var(--bg-black)] border border-[var(--border)] hover:bg-white/10 transition text-[var(--text-black)] text-sm"
            aria-label={t('sidebar.search.label')}
          >
            <LucideSearch size={16} className="text-[var(--text-black)] mr-2 flex-shrink-0" />
            <span className="truncate opacity-70">
              {t('sidebar.search.placeholder')}
            </span>
          </button>

          <nav className="flex flex-col items-start space-y-4 w-full flex-grow">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`flex items-start no-underline py-1 w-36 rounded-lg transition
                  ${isActive(item.path)
                    ? 'bg-[var(--button-hover)] text-pink-500'
                    : 'text-[var(--text-dark)] hover:bg-[var(--button-hover)]'}`}
                onClick={() => window.innerWidth < 768 && setIsSidebarOpen(false)}
              >
                <div className="text-xl mr-3">{item.icon}</div>
                <span className="text-sm font-sm whitespace-nowrap hidden md:block lg:text-base">
                  {t(`sidebar.navigation.${item.name}`)}
                </span>
              </Link>
            ))}
          </nav>

          <div className="mt-auto flex flex-col items-start w-full space-y-4">
            <button
              onClick={toggleTheme}
              className="p-2.5 bg-[var(--bg-black)] text-[var(--text-black)] border border-[var(--border)] rounded-full flex items-center justify-center"
              title={t('sidebar.theme.toggle')}
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <button
              onClick={() => {
                navigate('/login');
                if (window.innerWidth < 768) setIsSidebarOpen(false);
              }}
              className="w-36 px-8 text-left py-2 border-none bg-pink-500 text-white rounded-lg font-medium hover:bg-pink-600 transition shadow-md text-sm"
            >
              {t('sidebar.navigation.Login') || 'Login'}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}